import {
  Button,
  Card,
  CardBody,
  CardHeader,
  chakra,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Skeleton,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import CenteredAlert from "../../../../components/chakra/common/CenteredAlert";
import Editable from "../../../../components/chakra/common/Editable";
import {
  useApiMutation,
  useApiQuery,
} from "../../../../utilities/apibelRequest";
import useEditableState from "../../../../utilities/useEditableState";
import useToast from "../../../../utilities/useToast";
import ChangeEmailModal from "./ChangeEmailModal";
import ChangePasswordModal from "./ChangePasswordModal";

export default function UserDetailsCard() {
  const changePasswordModalState = useDisclosure({ defaultIsOpen: false });
  const changeEmailModalState = useDisclosure({ defaultIsOpen: false });
  const queryClient = useQueryClient();

  const { displayToast } = useToast();

  const userDetails = useApiQuery("user/profile", null)

  const updateUserDetailsMutation = useApiMutation(
    "userSettings/updateDetails",
  );

  const firstNameState = useEditableState(userDetails.data?.firstName || "");
  const lastNameState = useEditableState(userDetails.data?.lastName || "");
  const secondaryEmailState = useEditableState(
    userDetails.data?.secondaryEmail || "",
  );

  useEffect(() => {
    if (userDetails.data?.timezone) {
    }
  }, [userDetails.data]);

  const invalidateQuery = () => {
    queryClient.invalidateQueries(["user", "profile"]);
  };

  const handleUpdateFirstName = async () => {
    const firstName = firstNameState.currentValue;
    firstNameState.setIsLoading(true);
    try {
      const res = await updateUserDetailsMutation.mutateAsync({
        firstName,
      });
      if (res) {
        displayToast({
          status: "success",
          title: "First Name successfully updated",
        });
      }
      queryClient.setQueriesData<typeof userDetails.data>(
        ["user", "profile"],
        (prev) => (prev === undefined ? undefined : { ...prev, firstName }),
      );
      invalidateQuery();
    } catch (error) {
      console.warn(error);
      displayToast({
        status: "error",
        title: "Failed to update user details",
      });
      firstNameState.resetValue();
    }
    firstNameState.setIsLoading(false);
  };

  const handleUpdateLastName = async () => {
    const lastName = lastNameState.currentValue;
    lastNameState.setIsLoading(true);
    try {
      const res = await updateUserDetailsMutation.mutateAsync({
        lastName,
      });
      if (res) {
        displayToast({
          status: "success",
          title: "Last Name successfully updated",
        });
      }
      queryClient.setQueriesData<typeof userDetails.data>(
        ["user", "profile"],
        (prev) => (prev === undefined ? undefined : { ...prev, lastName }),
      );
      invalidateQuery();
    } catch (error) {
      console.warn(error);
      displayToast({
        status: "error",
        title: "Failed to update user details",
      });
      lastNameState.resetValue();
    }
    lastNameState.setIsLoading(false);
  };

  const handleUpdateSecondaryEmail = async () => {
    const secondaryEmail = secondaryEmailState.currentValue;
    secondaryEmailState.setIsLoading(true);
    try {
      const res = await updateUserDetailsMutation.mutateAsync({
        secondaryEmail,
      });
      if (res) {
        displayToast({
          status: "success",
          title: "Secondary Email successfully updated",
        });
      }
      queryClient.setQueriesData<typeof userDetails.data>(
        ["user", "profile"],
        (prev) =>
          prev === undefined ? undefined : { ...prev, secondaryEmail },
      );
      invalidateQuery();
    } catch (error) {
      console.warn(error);
      displayToast({
        status: "error",
        title: "Failed to update user details",
      });
      secondaryEmailState.resetValue();
    }
    secondaryEmailState.setIsLoading(false);
  };

  const form = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      roles: [],
    },
  });
  const { handleSubmit } = form;

    return (
    <>
      <ChangePasswordModal
        onRefresh={invalidateQuery}
        modalState={changePasswordModalState}
      />
      <ChangeEmailModal
        onRefresh={invalidateQuery}
        modalState={changeEmailModalState}
      />
      <VStack align="start" flex="1" minW="250px">
        <Heading size="md">Your Details</Heading>

        <VStack align="start" w="full" spacing={4}>
          {userDetails.isLoading ? (
            <>
              <Skeleton h="8" w="20%" />
              <Skeleton h="6" w="40%" />
              <Skeleton h="8" w="30%" />


                <Skeleton h="8" w="20%" />
                <Skeleton h="8" w="30%" />
                <Skeleton h="8" w="80%" />
              </>
            ) : userDetails.isSuccess ? (
              <>
                <VStack align="start" w="full">
                  <Heading size="sm">Email</Heading>
                  <Text>{userDetails.data.email}</Text>
                  <Button
                    size="sm"
                    colorScheme="gray"
                    onClick={changeEmailModalState.onOpen}>
                    Change Email
                  </Button>
                  <Divider />
                </VStack>
                <VStack align="start" w="full">
                  <FormProvider {...form}>
                    <chakra.form w="full" onSubmit={handleSubmit(() => {})}>
                      <VStack w="full" align="start">
                        <FormControl>
                          <FormLabel>First Name</FormLabel>
                          <Editable
                            isLoading={firstNameState.isLoading}
                            isDisabled={userDetails.isFetching}
                            onSubmit={handleUpdateFirstName}
                            onCancel={firstNameState.resetValue}
                            value={firstNameState.currentValue}
                            onChange={(value) => firstNameState.setValue(value)}
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Last Name</FormLabel>
                          <Editable
                            isLoading={lastNameState.isLoading}
                            isDisabled={userDetails.isFetching}
                            onSubmit={handleUpdateLastName}
                            onCancel={lastNameState.resetValue}
                            value={lastNameState.currentValue}
                            onChange={(value) => lastNameState.setValue(value)}
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Secondary Contact Email</FormLabel>
                          <Editable
                            isLoading={secondaryEmailState.isLoading}
                            isDisabled={userDetails.isFetching}
                            placeholder="None"
                            onSubmit={handleUpdateSecondaryEmail}
                            onCancel={secondaryEmailState.resetValue}
                            value={secondaryEmailState.currentValue}
                            onChange={(value) =>
                              secondaryEmailState.setValue(value)
                            }
                          />
                          <FormHelperText>
                            A secondary email is optional, but it may be used by
                            us for verifying your identity if you lose access to
                            your account.
                          </FormHelperText>
                        </FormControl>
                      </VStack>
                    </chakra.form>
                  </FormProvider>
                  <Divider />
                </VStack>
                <VStack align="start" w="full">
                  <Heading size="sm">Security</Heading>
                  <Button
                    size="sm"
                    colorScheme="gray"
                    onClick={changePasswordModalState.onOpen}>
                    Change Password
                  </Button>
                  {userDetails.data.mfa ? (
                    <Text>
                      Multifactor Authentication is set up. If you need to reset
                      your Multifactor Authentication please contact our support
                    </Text>
                  ) : (
                    <Text>
                      Multifactor Authentication not set up. You will be asked
                      to setup Multifactor next time you log in to CertCrowd.
                    </Text>
                  )}
                </VStack>
              </>
            ) : (
              <CenteredAlert
                w="full"
                status="error"
                title="Something went wrong">
                Failed to load user details
              </CenteredAlert>
            )}
          </VStack>
        </VStack>
      </>
  );
}
